import React, { useState } from 'react'
import Page from '../../../components/page/Page'
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Divider,
  Typography,
  Link,
  Tooltip,
} from '@mui/material'
import dayjs from 'dayjs'
import { NavLink } from 'react-router-dom'
import AddIcon from '@mui/icons-material/Add'
import ConfirmSignupModal from '../components/ConfirmSignupModal'
import useClass from '../useClass'
import { useParams } from 'react-router-dom'
import { connect } from 'react-redux'
import Span from '../../../components/Span'
import FamilySignupModal from '../components/FamilySignupModal'
import useIsUserRestricted from '../../admin/users/user/useIsUserRestricted'

const Class = ({ currentUser }) => {
  const { id } = useParams()
  const [confirmSignup, setConfirmSignup] = useState(false)
  const [confirmFamilySignup, setConfirmFamilySignup] = useState(false)
  const { gymClass, loading, error } = useClass(id)
  const { isRestricted } = useIsUserRestricted(currentUser)

  const toggleModal = () => {
    currentUser.memberType === 'family' && currentUser.family
      ? setConfirmFamilySignup((ps) => !ps)
      : setConfirmSignup((ps) => !ps)
  }

  const isActive = currentUser
    ? currentUser.status === 'active' || currentUser.status === 'trialing'
    : false

  const isFull =
    !loading && !error
      ? gymClass.class.capacity <= gymClass.class.enrolledMembers.length
      : true

  return (
    <Page loading={loading} error={error}>
      {!loading && !error && (
        <>
          <Box
            sx={{
              background: `url(${gymClass.class.coverImage}), linear-gradient(270deg, rgba(0,0,0,1) 0%, rgba(0,0,0,0) 0%, rgba(0,0,0,0.3029586834733894) 23%, rgba(0,0,0,0.6559217436974789) 41%, rgba(0,0,0,1) 100%, rgba(0,0,0,0.6559217436974789) 100%);`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center',
              backgroundBlendMode: 'multiply',
              height: 300,
              backgroundSize: 'cover',
              borderRadius: 1,
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-end',
            }}
          >
            <Typography variant='h4' color={'white.main'}>
              {gymClass.class.title}
            </Typography>
            <Typography
              mb={1}
              variant='body2'
              fontWeight={'light'}
              textTransform='capitalize'
            >
              {gymClass.user.brand ??
                `${gymClass.user.firstName} ${gymClass.user.lastName}`}
            </Typography>
            <Box display={'flex'} gap={1} mb={1} flexWrap={'wrap'}>
              {gymClass.class.tags.map((t, i) => {
                return (
                  <Chip
                    label={t}
                    key={i + t}
                    color='primary'
                    variant='outlined'
                    sx={{ borderRadius: 1, textTransform: 'capitalize' }}
                  />
                )
              })}
            </Box>
            <Typography textTransform={'uppercase'} mb={1}>
              {dayjs(gymClass.startTime.toDate()).format('ddd DD MMM hh:mm a')}{' '}
              |{' '}
              {dayjs(gymClass.endTime.toDate()).diff(
                dayjs(gymClass.startTime.toDate()),
                'minute'
              )}{' '}
              Minutes
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Box>
          <Typography maxWidth={'800px'} mb={2}>
            {gymClass.class.description}
          </Typography>
          <Box display={'flex'} gap={1} mb={2} flexWrap={'wrap'} maxWidth={500}>
            <Tooltip
              sx={{ fontSize: 24 }}
              title={
                isRestricted
                  ? 'A restriction applies to your account. Contact us to book a class.'
                  : null
              }
            >
              <Box sx={{ flexGrow: 1 }}>
                <Button
                  size='large'
                  variant='contained'
                  startIcon={!isRestricted && <AddIcon />}
                  onClick={toggleModal}
                  disabled={!isActive || isRestricted}
                  fullWidth
                >
                  {isActive
                    ? isRestricted
                      ? 'Account Restricted'
                      : isFull
                      ? 'Join waitlist'
                      : 'Sign me up'
                    : 'Members only'}
                </Button>
              </Box>
            </Tooltip>

            <Link to='/classes' sx={{ flexGrow: 1 }} component={NavLink}>
              <Button size='large' variant='outlined' fullWidth>
                View more classes
              </Button>
            </Link>
          </Box>
          {currentUser &&
            currentUser.memberType === 'family' &&
            !currentUser.family && (
              <Alert severity='info'>
                <AlertTitle>Trying to add family to classes?</AlertTitle>
                <Typography fontSize={14}>
                  It looks like you haven't setup your family members yet.{' '}
                  <Link component={NavLink} to={'/account?membership'}>
                    Visit your account
                  </Link>{' '}
                  to add family members then come back to this page to register
                  them on the class.
                </Typography>
              </Alert>
            )}
          {!isActive && (
            <Typography fontWeight={300} fontSize={14}>
              Not a member yet?{' '}
              <Link to='/join' component={NavLink}>
                <Span
                  sx={{
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Start your free trial now
                </Span>
              </Link>
            </Typography>
          )}
          {currentUser && (
            <>
              <ConfirmSignupModal
                isOpen={confirmSignup}
                onClose={toggleModal}
                gymClass={gymClass}
              />

              {currentUser.family && (
                <FamilySignupModal
                  isOpen={confirmFamilySignup}
                  onClose={toggleModal}
                  gymClass={gymClass}
                />
              )}
            </>
          )}
        </>
      )}
    </Page>
  )
}

const mapStateToProps = ({ user, subscription }) => ({
  currentUser: user.currentUser,
  subscriptionData: subscription.currentSubscription,
})

export default connect(mapStateToProps, null)(Class)
